import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown, FaChevronUp, FaCopy, FaCheck, FaTrash } from 'react-icons/fa';
import { MdBugReport } from 'react-icons/md';

const SESSION_KEY = '_portal_last_error';

const Badge = ({ children, color }) => {
  const colors = {
    red:    'tw-bg-red-100 tw-text-red-700 tw-border-red-200',
    amber:  'tw-bg-amber-100 tw-text-amber-700 tw-border-amber-200',
    blue:   'tw-bg-blue-100 tw-text-blue-700 tw-border-blue-200',
    slate:  'tw-bg-slate-100 tw-text-slate-600 tw-border-slate-200',
    green:  'tw-bg-green-100 tw-text-green-700 tw-border-green-200',
  };
  return (
    <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-sm tw-border tw-text-xs tw-font-mono tw-font-semibold ${colors[color] || colors.slate}`}>
      {children}
    </span>
  );
};

Badge.propTypes = { children: PropTypes.node, color: PropTypes.string };

const methodColor = (m) => ({ GET: 'blue', POST: 'green', PATCH: 'amber', PUT: 'amber', DELETE: 'red' }[m] || 'slate');

const Row = ({ label, children }) => (
  <div className="tw-flex tw-gap-3 tw-py-2 tw-border-b tw-border-slate-100 last:tw-border-b-0">
    <span className="tw-w-28 tw-shrink-0 tw-text-xs tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wide tw-pt-0.5">
      {label}
    </span>
    <div className="tw-flex-1 tw-min-w-0">{children}</div>
  </div>
);

Row.propTypes = { label: PropTypes.node, children: PropTypes.node };

const CodeBlock = ({ value }) => (
  <pre className="tw-m-0 tw-p-2 tw-bg-slate-900 tw-text-green-300 tw-text-xs tw-font-mono tw-rounded-md tw-overflow-x-auto tw-whitespace-pre-wrap tw-break-all tw-max-h-40">
    {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
  </pre>
);

// Arbitrary captured request/response payload: objects are pretty-printed.
CodeBlock.propTypes = { value: PropTypes.any };

const ErrorDetailPanel = () => {
  const [ctx, setCtx]         = useState(null);
  const [open, setOpen]       = useState(true);
  const [copied, setCopied]   = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) setCtx(JSON.parse(raw));
    } catch (_) { /* no saved context: show the panel without it */ }
  }, []);

  if (!ctx) return null;

  const fullApi = `${ctx.baseURL}${ctx.url}`;

  const statusColor = ctx.status >= 500 ? 'red' : ctx.status >= 400 ? 'amber' : 'slate';

  const copyReport = () => {
    const text = [
      `Error Report — ${new Date(ctx.timestamp).toLocaleString()}`,
      `Status  : ${ctx.status}`,
      `Page    : ${ctx.page}`,
      `API     : ${ctx.method} ${fullApi}`,
      `Message : ${ctx.message}`,
      ctx.detail ? `Detail  :\n${JSON.stringify(ctx.detail, null, 2)}` : '',
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const clear = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setCtx(null);
  };

  return (
    <div className="tw-w-full tw-max-w-2xl tw-mt-6">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2.5 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-t-lg tw-cursor-pointer tw-border-0 tw-transition-colors hover:tw-bg-amber-100"
        style={{ borderBottom: open ? 'none' : undefined }}
      >
        <span className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-amber-800">
          <MdBugReport size={16} />
          Error Details
        </span>
        <span className="tw-text-amber-500">
          {open ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
        </span>
      </button>

      {open && (
        <div className="tw-border tw-border-amber-200 tw-rounded-b-lg tw-bg-white tw-overflow-hidden">
          {/* Toolbar */}
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-4 tw-py-2 tw-bg-slate-50 tw-border-b tw-border-slate-100">
            <button
              type="button"
              onClick={copyReport}
              className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-500 hover:tw-text-slate-700 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors"
            >
              {copied ? <FaCheck size={11} className="tw-text-green-500" /> : <FaCopy size={11} />}
              {copied ? 'Copied!' : 'Copy report'}
            </button>
            <span className="tw-text-slate-200">|</span>
            <button
              type="button"
              onClick={clear}
              className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-400 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors"
            >
              <FaTrash size={10} /> Clear
            </button>
          </div>

          {/* Detail rows */}
          <div className="tw-px-4 tw-py-1">
            <Row label="Status">
              <Badge color={statusColor}>{ctx.status}</Badge>
            </Row>

            <Row label="Page">
              <span className="tw-text-xs tw-font-mono tw-text-slate-700 tw-break-all">{ctx.page}</span>
            </Row>

            <Row label="API">
              <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                <Badge color={methodColor(ctx.method)}>{ctx.method}</Badge>
                <span className="tw-text-xs tw-font-mono tw-text-slate-700 tw-break-all">{fullApi}</span>
              </div>
            </Row>

            <Row label="Message">
              <span className="tw-text-xs tw-text-slate-800">{ctx.message || '—'}</span>
            </Row>

            {ctx.detail && (
              <Row label="Response">
                <CodeBlock value={ctx.detail} />
              </Row>
            )}

            <Row label="Time">
              <span className="tw-text-xs tw-text-slate-500">
                {new Date(ctx.timestamp).toLocaleString('en-GB', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', second: '2-digit',
                })}
              </span>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorDetailPanel;
